import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * beHuman practitioners that look like a Zocdoc provider, keyed on NPI.
 *
 * The practitioner half of the patient match: an operator is offered the charts — here, the
 * practitioner records — that plausibly *are* this Zocdoc provider, and confirms one. Nothing
 * here writes anything; `zocdocLinkPractitioner` is the write.
 *
 * **NPI is the only key.** A clinician's name is not an identity ("Dr. Smith"), and a wrong
 * confirmation here would send a real booking to the wrong doctor's calendar, so a name is never
 * offered as a candidate. The consequence is that a provider Zocdoc gives no NPI for, or a
 * practitioner whose chart carries none, simply has no candidates — the operator maps that one by
 * hand, which is what the id field is still there for.
 *
 * `POST` rather than `GET` for the same reason as `zocdocProviderDetails`: the input is a list.
 */
export const zocdocPractitionerCandidates = oc
  .route({ method: 'POST', path: '/v2/zocdoc/practitioner-candidates' })
  .input(
    z.object({
      /** NPIs from the provider-link rows on screen. Duplicates are collapsed before the search. */
      npis: z.array(z.string().min(1)).min(1).max(100),
    }),
  )
  .output(
    z.object({
      ok: z.boolean(),
      /**
       * One entry per (NPI, practitioner) pair, flat rather than nested so a caller groups it the
       * way its own screen is laid out. An NPI with no match is simply absent.
       */
      candidates: z
        .array(
          z.object({
            /** The requested NPI this practitioner matched — how a caller joins back. */
            npi: z.string(),
            practitionerId: z.string(),
            /** Formatted for display; absent when the record carries no name. */
            name: z.string().optional(),
            /**
             * The organization whose member this practitioner is, via `PractitionerRole`. Shown
             * because a match in a *different* organization than the link's own is a real
             * possibility — every organization lives in one Medplum project — and it is the
             * operator, not this endpoint, who can tell whether that is right.
             */
            organizationId: z.string().optional(),
            /**
             * Whether `Schedule?actor=Practitioner/<id>` finds anything.
             *
             * Carried because a practitioner with no Schedule is the failure that surfaces late:
             * the link maps and enables cleanly, and then the first inbound booking has no Slot to
             * claim and no calendar to land on.
             */
            hasSchedule: z.boolean(),
            /**
             * The Zocdoc provider already written on this practitioner, when there is one. Three
             * states for the screen to tell apart, exactly as the appointment modal does for
             * charts: this provider (synced), another provider (never overwritten), or none
             * (offer the link).
             */
            zocdocProviderId: z.string().optional(),
          }),
        )
        .optional(),
      /**
       * Set when the practitioner lookup itself failed. Distinct from an empty `candidates`,
       * which means "searched, nothing matched" — an unread list must never render as "unmapped".
       */
      error: z.string().optional(),
    }),
  )
